import { ReactNode } from 'react';

import { OfficeHoursMessage } from '@backrs_ui/grow-ui/src/models/models';

import { Box, SxProps, Theme } from '@mui/material';

import { SendMessageParams } from './ChatBox';
import { DescribedSelectChatQuestion } from './DescribedSelectChatQuestion';
import { MultiNumericChatQuestion } from './MultiNumericChatQuestion';
import { MultiSelectChatQuestion } from './MultiSelectChatQuestion';
import { PromptButtons, PromptOption } from './PromptButtons';
import { SkipChatButton } from './SkipChatButton';
import { StepSliderChatQuestion } from './StepSliderChatQuestion';

type ComponentType = NonNullable<OfficeHoursMessage['componentType']>;

type StructuredQuestionInputProps = Readonly<{
  componentType: ComponentType;
  messageId: string;
  isTyping: boolean;
  onSelectOption: (option: string, additionalParams?: Partial<SendMessageParams>) => void;
}>;

/** Renders the input for a structured question component type; returns null for any other type. */
export const StructuredQuestionInput = ({
  componentType,
  messageId,
  isTyping,
  onSelectOption,
}: StructuredQuestionInputProps) => {
  const skipId = `chat-question-skip-button-${messageId}`;
  const handleSkip = () => onSelectOption('Skipped', { LLMInput: '<SKIP>' });

  if (componentType.type === 'SINGLE_SELECT') {
    const promptOptions: PromptOption[] = componentType.options.map((option, i) => ({
      id: String(i),
      title: option,
      hiddenPrompt: componentType.answerValue,
    }));

    const buttons = (
      <PromptButtons
        onClick={(option) =>
          onSelectOption(
            option.title,
            option.hiddenPrompt ? { LLMInput: option.hiddenPrompt } : undefined,
          )
        }
        isTyping={isTyping}
        promptOptions={promptOptions}
        variant="SINGLE_SELECT"
      />
    );

    if (componentType.required ?? true) {
      return buttons;
    }

    return withSkipFooter(buttons, skipId, isTyping, handleSkip);
  }

  if (componentType.type === 'STEP_SLIDER') {
    return (
      <StepSliderChatQuestion
        idPrefix={`step-slider-chat-question-${messageId}`}
        steps={componentType.options}
        sliders={componentType.sliders}
        ranking={componentType.ranking}
        required={componentType.required ?? true}
        isTyping={isTyping}
        onSubmit={(answer) => onSelectOption(answer)}
        onSkip={handleSkip}
      />
    );
  }

  if (componentType.type === 'SINGLE_SELECT_DESCRIBED') {
    return (
      <DescribedSelectChatQuestion
        idPrefix={`described-select-chat-question-${messageId}`}
        options={componentType.options}
        optionDescriptions={componentType.optionDescriptions}
        defaultValue={componentType.defaultValue}
        required={componentType.required ?? true}
        isTyping={isTyping}
        onSubmit={(selected) => onSelectOption(selected)}
        onSkip={handleSkip}
      />
    );
  }

  if (componentType.type === 'MULTI_SELECT') {
    return (
      <MultiSelectChatQuestion
        idPrefix={`multi-select-chat-question-${messageId}`}
        options={componentType.options}
        exclusiveOptions={componentType.exclusiveOptions}
        required={componentType.required ?? true}
        isTyping={isTyping}
        onSubmit={(selected) => onSelectOption(selected.join(', '))}
        onSkip={handleSkip}
      />
    );
  }

  if (componentType.type === 'MULTI_NUMERIC') {
    return (
      <MultiNumericChatQuestion
        idPrefix={`multi-numeric-chat-question-${messageId}`}
        options={componentType.options}
        subFields={componentType.subFields}
        required={componentType.required ?? false}
        isTyping={isTyping}
        onSubmit={(answer, displayText) =>
          onSelectOption(displayText, answer === displayText ? undefined : { LLMInput: answer })
        }
        onSkip={handleSkip}
      />
    );
  }

  if (componentType.type === 'SKIP_ONLY') {
    return (
      <Box sx={chatSkipRowSx}>
        <SkipChatButton id={skipId} disabled={isTyping} onSkip={handleSkip} />
      </Box>
    );
  }

  if (componentType.type === 'NUMERIC') {
    // The numeric value itself is typed into the main chat input; this slot only
    // surfaces a skip button when the question is optional.
    if (componentType.required) return null;

    return (
      <Box sx={chatSkipRowSx}>
        <SkipChatButton id={skipId} disabled={isTyping} onSkip={handleSkip} />
      </Box>
    );
  }

  return null;
};

const withSkipFooter = (
  content: ReactNode,
  skipId: string,
  isTyping: boolean,
  onSkip: () => void,
) => (
  <Box sx={chatQuestionWithSkipSx}>
    {content}
    <Box sx={chatSkipRowSx}>
      <SkipChatButton id={skipId} disabled={isTyping} onSkip={onSkip} />
    </Box>
  </Box>
);

const chatQuestionWithSkipSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const chatSkipRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
};
